class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);
    const inputWhitelist = [".facet", ".collection-actions-wrapper"];
    const inputBlacklist = [".collection-actions-compare-toggle"];

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 500);

    this.debouncedOnSubmitReduced = debounce(
      (event) => {
        this.onSubmitHandler(event);
      },
      250,
      true,
    );

    this.addEventListener("input", (event) => {
      if (
        !inputWhitelist.some((selector) => !!event.target.closest(selector)) ||
        inputBlacklist.some((selector) => !!event.target.closest(selector))
      ) {
        return false;
      }

      if (event.target.closest("price-range")) {
        this.debouncedOnSubmit(event);
      } else {
        this.debouncedOnSubmitReduced(event);
        // Filter model options when any filter changes (after debounce)
        setTimeout(() => {
          FacetFiltersForm.applyMotorcycleFinderFilters();
          FacetFiltersForm.filterModelOptions();
        }, 300);
      }
    });

    // Run filterModelOptions on initial load
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        setTimeout(() => {
          FacetFiltersForm.applyMotorcycleFinderFilters();
          FacetFiltersForm.filterModelOptions();
        }, 500);
      });
    } else {
      setTimeout(() => {
        FacetFiltersForm.applyMotorcycleFinderFilters();
        FacetFiltersForm.filterModelOptions();
      }, 500);
    }
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state
        ? event.state.searchParams
        : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) {
        return;
      }

      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener("popstate", onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll(".js-facet-remove").forEach((element) => {
      element.classList.toggle("disabled", disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true, appendResults) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById("ProductCount");
    document
      .getElementById("ProductGridContainer")
      .querySelector(".collection")
      .classList.add("loading");

    if (countContainer) {
      countContainer.classList.add("loading");
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event, appendResults);
    });

    if (updateURLHash) {
      FacetFiltersForm.updateURLHash(searchParams);
    }

    if (!searchParams.includes("page")) {
      publish(PUB_SUB_EVENTS.collectionFilters, {
        filters: deserializeSearchParams(searchParams),
      });
    }
  }

  static renderSectionFromFetch(url, event, appendResults) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderCollectionActions(html);
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html, appendResults);
        FacetFiltersForm.renderProductCount(html);
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderCollectionActions(html);
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
  }

  static renderProductGridContainer(html, appendResults) {
    const container = document.getElementById("ProductGridContainer");
    const parsedDom = new DOMParser().parseFromString(html, "text/html");

    if (appendResults) {
      parsedDom
        .getElementById("product-grid")
        .prepend(...[...document.getElementById("product-grid").childNodes]);
    }
    container.innerHTML = parsedDom.getElementById("ProductGridContainer").innerHTML;
  }

  static renderProductCount(html) {
    const count = new DOMParser()
      .parseFromString(html, "text/html")
      .getElementById("ProductCount")?.innerHTML;
    const container = document.getElementById("ProductCount");

    if (!container) {
      return;
    }

    container.innerHTML = count;
    container.classList.remove("loading");
  }

  static renderCollectionActions(html) {
    const container = document.getElementById("CollectionActions");
    const parsedDom = new DOMParser().parseFromString(html, "text/html");

    if (!container) {
      return;
    }

    container.classList = parsedDom.getElementById("CollectionActions").classList;
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, "text/html");

    const oldFacetElements = document.querySelectorAll("#FacetFiltersForm .js-filter");

    const newFacetElements = parsedHTML.querySelectorAll("#FacetFiltersForm .js-filter");

    const expandedFacets = [];
    document
      .querySelectorAll('#FacetFiltersForm .js-filter collapsible-expandable[expanded="true"]')
      ?.forEach((node) => {
        const parent = node.closest(".js-filter");
        if (node.getAttribute("expanded")) {
          expandedFacets.push(parent.dataset.name);
        }
      });

    const facetsContainer = document.querySelector(
      "#FacetFiltersForm .page-layout-sidebar-inner-content",
    );
    const focusedElement = document.activeElement;
    let focusedFilterId = null;
    if (facetsContainer?.contains(focusedElement)) {
      focusedFilterId = focusedElement.id;
    }

    if (facetsContainer) {
      const previousHeight = facetsContainer?.getBoundingClientRect().height;
      facetsContainer.style.minHeight = `${previousHeight}px`;
    }

    oldFacetElements.forEach((facet) => facet.remove());
    newFacetElements.forEach((element) => {
      const isExpanded = expandedFacets.includes(element.dataset.name);
      element.querySelector(".facet-toggle").setAttribute("aria-expanded", isExpanded);
      element.querySelector("collapsible-expandable").setAttribute("expanded", isExpanded);
      facetsContainer.append(element);
    });

    if (focusedFilterId) {
      document.getElementById(focusedFilterId)?.focus({ preventScroll: true });
    }

    if (facetsContainer) {
      requestAnimationFrame(() => {
        facetsContainer.style.minHeight = "";
      });
    }

    const scrollTop = document.querySelector("facet-filters-form").dataset.scrollTop === "true";
    const container = document.getElementById("CollectionActions");
    if (
      scrollTop &&
      container &&
      window.innerWidth >= 990 &&
      !event?.target.name.includes(".price.")
    ) {
      const offset = document.querySelector("sticky-header:not([disabled])")?.offsetHeight ?? 0;
      const rect = container.getBoundingClientRect();
      const scrollPosition = window.scrollY + rect.top - offset;
      if (rect.top < 0) {
        window.scrollTo({
          top: scrollPosition,
          behavior: "smooth",
        });
      }
    }

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.applyMotorcycleFinderFilters();
    FacetFiltersForm.filterModelOptions();
  }

  static applyMotorcycleFinderFilters() {
    // Check if we have motorcycle finder URL parameters
    const urlParams = new URLSearchParams(window.location.search);
    const searchYear = urlParams.get('year');
    const searchMake = urlParams.get('make');
    const searchModel = urlParams.get('model');
    
    // If no motorcycle finder parameters, remove any hidden classes and do nothing
    if (!searchYear && !searchMake && !searchModel) {
      document.querySelectorAll('#FacetFiltersForm .checkbox-facet').forEach((option) => {
        option.classList.remove('visually-hidden');
      });
      return false; // Return false to indicate no filters were applied
    }
    
    // Find Year, Make, and Model filters
    const filters = {
      year: null,
      make: null,
      model: null
    };
    
    document.querySelectorAll('#FacetFiltersForm .js-filter').forEach((filter) => {
      const label = filter.querySelector('.facet-toggle span')?.textContent?.trim().toLowerCase();
      if (label === 'year' || label === 'years') {
        filters.year = filter;
      } else if (label === 'make' || label === 'makes') {
        filters.make = filter;
      } else if (label === 'model' || label === 'models') {
        filters.model = filter;
      }
    });
    
    // Helper function to auto-check matching facet options and hide non-matching ones
    const checkAndFilterFacetOption = (filter, searchValue) => {
      if (!filter || !searchValue) return;
      
      const searchValueLower = searchValue.toLowerCase().trim();
      filter.querySelectorAll('.checkbox-facet').forEach((option) => {
        const checkbox = option.querySelector('input[type="checkbox"]');
        const label = option.querySelector('.checkbox-facet-label-text')?.textContent?.trim().toLowerCase();
        
        if (label && label === searchValueLower) {
          // Check the matching option
          if (checkbox && !checkbox.checked && !checkbox.disabled) {
            checkbox.checked = true;
          }
          // Make sure it's visible
          option.classList.remove('visually-hidden');
        } else {
          // Hide non-matching options
          option.classList.add('visually-hidden');
        }
      });
    };
    
    // Apply filters for Year, Make, and Model (check them and hide non-matching options)
    checkAndFilterFacetOption(filters.year, searchYear);
    checkAndFilterFacetOption(filters.make, searchMake);
    checkAndFilterFacetOption(filters.model, searchModel);
    
    return true; // Return true to indicate filters were applied
  }

  static filterModelOptions() {
    // Skip this filtering if motorcycle finder parameters are present
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('year') || urlParams.get('make') || urlParams.get('model')) {
      return; // Let the motorcycle finder filters handle everything
    }
    
    // Find the Year, Make and Model filters
    const yearFilter = Array.from(document.querySelectorAll('#FacetFiltersForm .js-filter')).find(
      (filter) => {
        const label = filter.querySelector('.facet-toggle span')?.textContent?.trim().toLowerCase();
        return label === 'year' || label === 'years';
      }
    );
    
    const makeFilter = Array.from(document.querySelectorAll('#FacetFiltersForm .js-filter')).find(
      (filter) => {
        const label = filter.querySelector('.facet-toggle span')?.textContent?.trim().toLowerCase();
        return label === 'make' || label === 'makes' || label.startsWith('make');
      }
    );

    const modelFilter = Array.from(document.querySelectorAll('#FacetFiltersForm .js-filter')).find(
      (filter) => {
        const label = filter.querySelector('.facet-toggle span')?.textContent?.trim().toLowerCase();
        return label === 'model' || label === 'models' || label.startsWith('model');
      }
    );

    if (!modelFilter) {
      return;
    }

    // Get selected years
    const yearCheckboxes = yearFilter?.querySelectorAll('input[type="checkbox"]:checked') || [];
    const selectedYears = Array.from(yearCheckboxes).map((cb) => {
      const label = cb.closest('label')?.querySelector('.checkbox-facet-label-text')?.textContent?.trim();
      return parseInt(label);
    }).filter(year => !isNaN(year));

    // Get selected makes
    const makeCheckboxes = makeFilter?.querySelectorAll('input[type="checkbox"]:checked') || [];
    const hasMakeSelection = makeCheckboxes.length > 0;
    const selectedMakes = Array.from(makeCheckboxes).map((cb) => {
      const label = cb.closest('label')?.querySelector('.checkbox-facet-label-text')?.textContent?.trim();
      return label?.toLowerCase();
    });

    // If no Make or Year is selected, show all models (except those with count 0)
    if (!hasMakeSelection && selectedYears.length === 0) {
      modelFilter.querySelectorAll('.checkbox-facet').forEach((option) => {
        const checkbox = option.querySelector('input[type="checkbox"]');
        const countElement = option.querySelector('.checkbox-facet-label-count');
        const count = countElement ? parseInt(countElement.textContent.trim()) : 0;
        
        // Only hide if count is 0 and not checked
        if (count === 0 && !checkbox?.checked && checkbox?.disabled) {
          option.classList.add('visually-hidden');
        } else {
          option.classList.remove('visually-hidden');
        }
      });
      return;
    }

    // Use motorcycle database if available to filter by make AND year
    if (typeof motorcycleDatabase !== 'undefined' && hasMakeSelection) {
      // Debug logging
      console.log('Filtering models with:', { selectedMakes, selectedYears });
      
      modelFilter.querySelectorAll('.checkbox-facet').forEach((option) => {
        const checkbox = option.querySelector('input[type="checkbox"]');
        const modelLabel = option.querySelector('.checkbox-facet-label-text')?.textContent?.trim();
        const countElement = option.querySelector('.checkbox-facet-label-count');
        const count = countElement ? parseInt(countElement.textContent.trim()) : 0;

        // Always hide if count is 0 and not checked
        if (count === 0 && !checkbox?.checked) {
          option.classList.add('visually-hidden');
          return;
        }

        // If checked, always show
        if (checkbox?.checked) {
          option.classList.remove('visually-hidden');
          return;
        }

        // Check if this model exists for the selected make(s) and year(s)
        let isValidModel = false;

        for (const selectedMake of selectedMakes) {
          if (!selectedMake) continue;
          
          // Find the exact make name from the database (case-insensitive match)
          const makeCapitalized = motorcycleDatabase.makes.find(
            make => make.toLowerCase() === selectedMake.toLowerCase()
          ) || selectedMake;

          // If years are selected, check if the model exists for those years
          if (selectedYears.length > 0) {
            for (const selectedYear of selectedYears) {
              const models = motorcycleDatabase.getModels(makeCapitalized, selectedYear);
              console.log(`Models for ${makeCapitalized} ${selectedYear}:`, models);
              console.log(`Checking if "${modelLabel}" matches any model`);
              
              // More flexible matching - check if model label contains or is contained by database model
              if (models.some(model => {
                const modelLower = model.toLowerCase();
                const labelLower = modelLabel?.toLowerCase();
                const matches = modelLower === labelLower || 
                       labelLower?.includes(modelLower) || 
                       modelLower.includes(labelLower);
                if (matches) {
                  console.log(`  ✓ "${modelLabel}" matches "${model}"`);
                }
                return matches;
              })) {
                isValidModel = true;
                break;
              }
            }
          } else {
            // No year selected, just check if model belongs to the make
            const models = motorcycleDatabase.getModels(makeCapitalized);
            console.log(`Models for ${makeCapitalized} (all years):`, models);
            console.log(`Checking if "${modelLabel}" matches any model`);
            
            // More flexible matching
            if (models.some(model => {
              const modelLower = model.toLowerCase();
              const labelLower = modelLabel?.toLowerCase();
              const matches = modelLower === labelLower || 
                     labelLower?.includes(modelLower) || 
                     modelLower.includes(labelLower);
              if (matches) {
                console.log(`  ✓ "${modelLabel}" matches "${model}"`);
              }
              return matches;
            })) {
              isValidModel = true;
              break;
            }
          }

          if (isValidModel) break;
        }

        // Show or hide based on whether it's valid
        if (isValidModel) {
          option.classList.remove('visually-hidden');
        } else {
          console.log(`  ✗ "${modelLabel}" - no match, hiding`);
          option.classList.add('visually-hidden');
        }
      });
    } else if (hasMakeSelection) {
      // Fallback to simple mapping if database is not available
      const makeModelMapping = {
        'ducati': ['748', '749', '848', '916', '996', '998', '999', '1098', '1198', '1199', '1299', 'panigale', 'monster', 'multistrada', 'hypermotard', 'scrambler', 'diavel', 'xdiavel', 'superleggera', 'streetfighter', 'supersport', 'v2', 'v4'],
        'mv agusta': ['brutale', 'dragster', 'f3', 'f4', 'rush', 'superveloce', 'brutale 800', 'brutale 1000', 'superveloce 800', 'superveloce 1000'],
        'aprilia': ['rsv4'],
      };

      modelFilter.querySelectorAll('.checkbox-facet').forEach((option) => {
        const checkbox = option.querySelector('input[type="checkbox"]');
        const modelLabel = option.querySelector('.checkbox-facet-label-text')?.textContent?.trim().toLowerCase();
        const countElement = option.querySelector('.checkbox-facet-label-count');
        const count = countElement ? parseInt(countElement.textContent.trim()) : 0;

        // Always hide if count is 0 and not checked
        if (count === 0 && !checkbox?.checked) {
          option.classList.add('visually-hidden');
          return;
        }

        // If checked, always show
        if (checkbox?.checked) {
          option.classList.remove('visually-hidden');
          return;
        }

        // Check if this model belongs to any of the selected makes
        let belongsToSelectedMake = false;
        for (const selectedMake of selectedMakes) {
          if (!selectedMake) continue;
          
          const modelsForMake = makeModelMapping[selectedMake] || [];
          
          if (modelsForMake.some(model => {
            const modelLower = model.toLowerCase();
            return modelLabel === modelLower || modelLabel.includes(modelLower) || modelLower.includes(modelLabel);
          })) {
            belongsToSelectedMake = true;
            break;
          }
        }

        // Show or hide based on whether it belongs to the make
        if (belongsToSelectedMake) {
          option.classList.remove('visually-hidden');
        } else {
          option.classList.add('visually-hidden');
        }
      });
    }
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = [".collection-actions-filters"];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) {
        return;
      }

      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static updateURLHash(searchParams, replace) {
    if (replace) {
      history.replaceState(
        { searchParams },
        "",
        `${window.location.pathname}${searchParams && "?".concat(searchParams)}`,
      );
    } else {
      history.pushState(
        { searchParams },
        "",
        `${window.location.pathname}${searchParams && "?".concat(searchParams)}`,
      );
    }
  }

  static getSections() {
    return [
      {
        section: document.getElementById("product-grid").dataset.id,
      },
    ];
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const formData = new FormData(document.getElementById("FacetsFilterForm"));
    const searchParams = new URLSearchParams(formData).toString();
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onActiveFilterClick(event) {
    // Check if this is a motorcycle finder clear - if so, force navigation
    if (event.currentTarget.hasAttribute('data-motorcycle-clear')) {
      event.preventDefault();
      window.location.href = event.currentTarget.getAttribute('href');
      return;
    }
    
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf("?") == -1
        ? ""
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf("?") + 1);
    FacetFiltersForm.renderPage(url);
  }

  onAjaxPagination(event) {
    event.preventDefault();
    const next = event.currentTarget;
    const url = next.href.indexOf("?") == -1 ? "" : next.href.slice(next.href.indexOf("?") + 1);
    FacetFiltersForm.renderPage(url, event, true);
    scrollToElement(document.getElementById("ProductGridContainer"), -150);

    const params = new URLSearchParams(url);
    publish(PUB_SUB_EVENTS.pageChange, {
      page: Number(params.get("page")),
    });
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define("facet-filters-form", FacetFiltersForm);
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll("input").forEach((element) =>
      element.addEventListener("change", this.onRangeChange.bind(this)),
    );

    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll("input");
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute("max", maxInput.value);
    if (minInput.value) maxInput.setAttribute("min", minInput.value);
    if (minInput.value === "") maxInput.setAttribute("min", 0);
    if (maxInput.value === "") minInput.setAttribute("max", maxInput.getAttribute("max"));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute("min"));
    const max = Number(input.getAttribute("max"));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define("price-range", PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const link = this.querySelector("a");
    
    link.addEventListener("click", (event) => {
      const urlParams = new URLSearchParams(window.location.search);
      const searchQuery = urlParams.get('q') || '';
      const hasYear = urlParams.has('year');
      const hasMake = urlParams.has('make');
      const hasModel = urlParams.has('model');
      const isBikeSearch = searchQuery.includes('bike:');
      const isMotorcycleSearch = isBikeSearch || (hasYear && hasMake && hasModel);
      
      // Check if this is a motorcycle search - Clear All should go to all-products
      if (isMotorcycleSearch && link.textContent.trim().toLowerCase().includes('clear')) {
        event.preventDefault();
        event.stopImmediatePropagation();
        window.location.href = '/collections/all-products';
        return false;
      }
      
      // Check if link explicitly has the data attribute
      if (link.hasAttribute('data-motorcycle-clear')) {
        event.preventDefault();
        event.stopImmediatePropagation();
        window.location.href = link.getAttribute('href');
        return false;
      }
      
      event.preventDefault();
      const form =
        this.closest("facet-filters-form") || document.querySelector("facet-filters-form");
      form.onActiveFilterClick(event);
    }, true); // Use capture phase to run before other listeners
  }
}

customElements.define("facet-remove", FacetRemove);

class AjaxPaginate extends HTMLElement {
  constructor() {
    super();
    const form = this.closest("facet-filters-form") || document.querySelector("facet-filters-form");
    const buttons = this.querySelectorAll("a");

    buttons.forEach((button) => {
      button.addEventListener("click", (event) => {
        event.preventDefault();
        form.onAjaxPagination(event);
      });
    });
  }
}

customElements.define("ajax-paginate", AjaxPaginate);

function scrollToElement(element, offset) {
  const elementRect = element.getBoundingClientRect();
  const absoluteElementTop = elementRect.top + window.pageYOffset;
  window.scrollTo({
    top: absoluteElementTop + offset,
    behavior: "smooth",
  });
}

/**
 * Accepts a query string from faceted filtering and returns a
 * deserialized object.
 *
 * @param {string} queryString The query string.
 * @returns {{}}
 */
const deserializeSearchParams = (queryString) => {
  const params = new URLSearchParams(queryString);
  const result = {};

  for (const [key, value] of params.entries()) {
    if (result[key]) {
      result[key] = `${result[key]},${value}`;
    } else if (value) {
      result[key] = value;
    }
  }

  return result;
};
